import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { faqList } from '@/mocks/homeData';

export default function FaqSection() {
  const { t } = useTranslation();
  const sectionRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [expandedIndex, setExpandedIndex] = useState<number | null>(0);
  const [currentPage, setCurrentPage] = useState(0);
  const itemsPerPage = 4;
  const totalPages = Math.ceil(faqList.length / itemsPerPage);
  const pageItems = faqList.slice(
    currentPage * itemsPerPage,
    currentPage * itemsPerPage + itemsPerPage
  );

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const toggleExpand = (index: number) => {
    setExpandedIndex((prev) => (prev === index ? null : index));
  };

  const goToPage = (page: number) => {
    setCurrentPage(page);
    setExpandedIndex(null);
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqList.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };

  return (
    <section
      id="faq"
      ref={sectionRef}
      className="relative w-full py-16 md:py-24 bg-background-50"
    >
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />

      <div className="w-full px-4 md:px-8 lg:px-12 max-w-7xl mx-auto">
        <div className="text-center mb-10 md:mb-14">
          <span className="inline-block text-accent-600 text-sm font-medium tracking-wider uppercase mb-2">
            FAQ
          </span>
          <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950">
            {t('faq.title')}
          </h2>
          <p className="mt-3 text-sm md:text-base text-foreground-500">
            {t('faq.subtitle')}
          </p>
        </div>

        <div
          className={`max-w-3xl mx-auto space-y-3 transition-all duration-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          {pageItems.map((item, index) => {
            const isExpanded = expandedIndex === index;
            return (
              <div
                key={index}
                className={`bg-background-100 rounded-xl border transition-all duration-300 ${
                  isExpanded
                    ? 'border-accent-300 shadow-sm'
                    : 'border-background-200/70 hover:border-accent-300'
                }`}
              >
                <button
                  type="button"
                  onClick={() => toggleExpand(index)}
                  aria-expanded={isExpanded}
                  className="w-full flex items-center gap-4 px-5 md:px-6 py-4 md:py-5 text-left cursor-pointer"
                >
                  <span className="flex-1 font-heading text-base md:text-lg font-bold text-foreground-950 leading-snug">
                    {item.question}
                  </span>
                  <span
                    className={`shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-background-200/70 text-foreground-700 transition-transform duration-300 ${
                      isExpanded ? 'rotate-180' : ''
                    }`}
                  >
                    <i className="ri-arrow-down-s-line text-lg" />
                  </span>
                </button>
                <div
                  className={`grid transition-all duration-300 ease-in-out ${
                    isExpanded
                      ? 'grid-rows-[1fr] opacity-100'
                      : 'grid-rows-[0fr] opacity-0'
                  }`}
                >
                  <div className="overflow-hidden">
                    <p className="px-5 md:px-6 pb-5 text-sm md:text-base text-foreground-600 leading-relaxed">
                      {item.answer}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {totalPages > 1 && (
          <div className="mt-8 md:mt-10 flex items-center justify-center gap-3">
            <button
              type="button"
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage === 0}
              className="w-10 h-10 flex items-center justify-center rounded-md border border-background-200/70 bg-background-100 text-foreground-700 transition-colors hover:border-accent-300 hover:text-accent-600 disabled:opacity-40 disabled:cursor-not-allowed"
              aria-label="이전 질문"
            >
              <i className="ri-arrow-left-s-line text-lg" />
            </button>
            {Array.from({ length: totalPages }).map((_, page) => (
              <button
                key={page}
                type="button"
                onClick={() => goToPage(page)}
                className={`w-10 h-10 flex items-center justify-center rounded-md text-sm font-medium transition-colors ${
                  page === currentPage
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 border border-background-200/70 hover:border-accent-300 hover:text-accent-600'
                }`}
                aria-label={`${page + 1} 페이지`}
                aria-current={page === currentPage ? 'page' : undefined}
              >
                {page + 1}
              </button>
            ))}
            <button
              type="button"
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage === totalPages - 1}
              className="w-10 h-10 flex items-center justify-center rounded-md border border-background-200/70 bg-background-100 text-foreground-700 transition-colors hover:border-accent-300 hover:text-accent-600 disabled:opacity-40 disabled:cursor-not-allowed"
              aria-label="다음 질문"
            >
              <i className="ri-arrow-right-s-line text-lg" />
            </button>
          </div>
        )}
      </div>
    </section>
  );
}